import {
  ACTIONS_COLUMN_ID,
  type BodyColumnDef,
  DataTable,
  Link,
  TruncatedTextWithTooltip,
} from "@components/ui-kit";
import { t } from "@lingui/macro";
import { BrandCreditAccountOrderByColumnEnum } from "@src/__generated__/urql-graphql";
import { currency } from "@src/utils/formatters";
import { filter } from "lodash";
import { observer } from "mobx-react-lite";
import { useContext, useMemo } from "react";
import type { CreditAccountModel } from "../models";
import { PageCreditAccountsCtx } from "../PageCreditAccounts";
import { PopoverRowActions } from "./PopoverRowActions";
import s from "./TableCreditAccounts.module.css";

type TData = {
  id: string;
  brand?: CreditAccountModel["brand"];
  title: CreditAccountModel["title"];
  budgetCategory:
    | CreditAccountModel["expenseBudgetCategories"]
    | CreditAccountModel["ourWorkBudgetCategories"];
  remainingCredit: CreditAccountModel["stats"]["remaining_credit"];
  totalCredit: CreditAccountModel["stats"]["total_credit"];
  totalDebit: CreditAccountModel["stats"]["total_debit"];
  actions: string;
  _model: CreditAccountModel;
};

const getColumns = (): BodyColumnDef<
  TData,
  BrandCreditAccountOrderByColumnEnum
>[] => [
  {
    id: "id",
    meta: { label: t`ID` },
    header: () => t`ID`,
    cell: ({ row }) => row.original.id,
  },
  {
    id: "brand",
    meta: { label: t`Brand` },
    header: () => t`Brand`,
    orderBy: BrandCreditAccountOrderByColumnEnum.BrandName,
    cell: ({ row }) => {
      const r = row.original;

      return (
        <div className="flex items-center">
          {r.brand && (
            <Link
              href={{ pathname: "/brands/detail", query: { id: r.brand.id } }}
            >
              {r.brand.name}
            </Link>
          )}
        </div>
      );
    },
  },
  {
    id: "title",
    meta: { label: t`Title` },
    header: () => t`Title`,
    orderBy: BrandCreditAccountOrderByColumnEnum.Title,
    cell: ({ row }) => {
      const r = row.original;

      return (
        <div className="flex items-center">
          <Link
            className={s.limitCell}
            href={{ pathname: "/credit-accounts/detail", query: { id: r.id } }}
          >
            {r.title}
          </Link>
        </div>
      );
    },
  },
  {
    id: "budgetCategory",
    meta: { label: t`Budget category` },
    header: () => t`Budget category`,
    cell: ({ row }) => {
      const r = row.original;
      const categories = r.budgetCategory.map((c) => c.name);
      categories.sort();
      const categoriesText = categories.join(", ");

      return <TruncatedTextWithTooltip text={categoriesText} />;
    },
  },
  {
    id: "totalCredit",
    meta: { align: "right", label: t`Total credit` },
    header: () => t`Total credit`,
    orderBy: BrandCreditAccountOrderByColumnEnum.TotalCredit,
    cell: ({ row }) => {
      const r = row.original;

      return currency.formatByWorkspace(r.totalCredit);
    },
  },
  {
    id: "totalDebit",
    meta: { align: "right", label: t`Total debit` },
    header: () => t`Total debit`,
    orderBy: BrandCreditAccountOrderByColumnEnum.TotalDebit,
    cell: ({ row }) => {
      const r = row.original;

      return currency.formatByWorkspace(r.totalDebit);
    },
  },
  {
    id: "remainingCredit",
    meta: { align: "right", label: t`Remaining credit` },
    header: () => t`Remaining credit`,
    orderBy: BrandCreditAccountOrderByColumnEnum.RemainingCredit,
    cell: ({ row }) => {
      const r = row.original;

      return <b>{currency.formatByWorkspace(r.remainingCredit)}</b>;
    },
  },
  {
    id: ACTIONS_COLUMN_ID,
    meta: { label: t`Actions` },
    header: () => t`Actions`,
    cell: ({ row }) => (
      <PopoverRowActions creditAccount={row.original._model} />
    ),
  },
];

export const TableCreditAccounts = observer(function TableCreditAccounts() {
  const store = useContext(PageCreditAccountsCtx);

  const data: TData[] = store.creditAccounts.map((i) => ({
    id: i.id,
    brand: i.brand,
    title: i.title,
    budgetCategory: [
      ...i.expenseBudgetCategories,
      ...i.ourWorkBudgetCategories,
    ],
    remainingCredit: i.stats.remaining_credit,
    totalCredit: i.stats.total_credit,
    totalDebit: i.stats.total_debit,
    actions: "",
    _model: i,
  }));

  const cols = useMemo(() => {
    const columns = getColumns();

    return store.asExternalModule
      ? filter(
          columns,
          (col) => !store.hideColsInExternalModule.all.includes(String(col.id)),
        )
      : columns;
  }, [store.asExternalModule]);

  return (
    <DataTable.Body
      tableKey="credit-accounts"
      data={data}
      columns={cols}
      orderedBy={store.orderBy.clauses[0]}
      onOrderByChange={(clause) => {
        if (!clause.order) {
          store.orderBy.setClauses([]);
          return;
        }

        store.orderBy.setClauses([
          { column: clause.column, order: clause.order },
        ]);
      }}
    />
  );
});
